/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/components/ColumnHeader/Trigger
 * @desc Default DataGrid ColumnHeader
 */

import classnames from 'classnames';
import { useDataGridContext } from 'labstep-web/contexts/grid/hook';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { PopupContainer } from 'labstep-web/core/Popup';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel';
import { Protocol } from 'labstep-web/models/protocol.model';
import type React from 'react';
import { useRef } from 'react';
import styles from './styles.module.scss';
import type { GridSmartTableColumnHeaderTriggerProps } from './types';

export const GridSmartTableColumnHeaderTrigger: React.FC<
  GridSmartTableColumnHeaderTriggerProps
> = ({
  onClick,
  displayName,
  icon,
  cornerIcon,
  action,
  column,
  secondaryHeader,
  experimentOrProtocol,
  isAddColumn = false,
  metadata,
  protocolValue,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { agGrid } = useDataGridContext();
  const toolPanelContext = useToolPanelContext();

  const openSidePanel = () => {
    if (agGrid && toolPanelContext) {
      toolPanelContext.setToolPanelState(ToolPanelState.add_column);
      agGrid.api.setSideBarVisible(!agGrid.api.isSideBarVisible());
    }
  };

  const colDef = column.getColDef() as SmartTableColDef;

  const fieldNotFound =
    colDef.colId &&
    colDef.colId.startsWith('metadata:') &&
    colDef.colId.split(':')[1] === 'experiment' &&
    !metadata;

  return (
    <div
      ref={ref}
      className={classnames(styles.defaultContainer, {
        [styles.clickable]: !!onClick || isAddColumn,
      })}
      data-cy={`grid/SmartTableProtocolValue/components/ColumnHeader/Trigger${isAddColumn ? ':add-column' : ''}`}
      onClick={isAddColumn ? openSidePanel : onClick}
    >
      <div
        className={isAddColumn ? styles.actionTitle : styles.title}
      >
        {icon && <Icon name={icon} cornerIcon={cornerIcon} />}{' '}
        <Flex column>
          {displayName}
          {secondaryHeader && (
            <span className={styles.secondaryHeader}>
              {secondaryHeader}
            </span>
          )}
        </Flex>
      </div>
      {fieldNotFound && (
        <PopupContainer
          trigger={<Icon name="info circle" color="red" />}
          position="bottom center"
          content={`Field not found`}
        />
      )}
      {experimentOrProtocol instanceof Protocol &&
        colDef.colId?.endsWith('resource_item_name') && (
          <PopupContainer
            trigger={<Icon name="info circle" color="yellow" />}
            position="bottom center"
            content={`Items can only be created when running the protocols`}
          />
        )}
      {action}
    </div>
  );
};
